import { useProductsWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';
import { dataMapMerchandisingAssociation } from '@/data/Content/MerchandisingAssociation';
import { ProductType } from '@/data/types/Product';
import { useMemo } from 'react';

export const MERCHANDISING_ASSOCIATION_TYPE_COLOR = 'COLOR';

export const useProductExtractMerchandisingAssociations = (product?: ProductType) => {
	const productVariantColors = useMemo<ProductType[]>(() => {
		if (!product) {
			return [];
		}

		const associations = dataMapMerchandisingAssociation(product);

		if (!associations?.length) {
			return [];
		}

		const productColors = associations.filter(
			(association) => association.associationType === MERCHANDISING_ASSOCIATION_TYPE_COLOR
		);

		if (productColors.length === 0) {
			return [];
		}

		const hasParent = productColors.some(
			(association) => association.partNumber === product.partNumber
		);

		if (!hasParent) {
			productColors.unshift({
				...product,
				associationType: MERCHANDISING_ASSOCIATION_TYPE_COLOR,
			});
		}

		return productColors;
	}, [product]);

	return { productVariantColors };
};

export const useBaseFirstColorVariants = (product?: ProductType) => {
	const { productVariantColors } = useProductExtractMerchandisingAssociations(product);

	return useMemo<ProductType[]>(() => {
		if (!product) {
			return productVariantColors;
		}

		const base = productVariantColors.filter(
			(variant) => variant.partNumber === product.partNumber
		);
		const rest = productVariantColors.filter(
			(variant) => variant.partNumber !== product.partNumber
		);

		return [...base, ...rest];
	}, [productVariantColors, product]);
};

export const useProductExtractMerchandisingAssociationsWithInfoElectronico = (
	product?: ProductType
) => {
	const { productVariantColors: rawProductVariantColors } =
		useProductExtractMerchandisingAssociations(product);

	const { data, isLoading } = useProductsWithInfoElectronico(rawProductVariantColors);

	const { data: currentProducts, isLoading: isLoadingCurrent } = useProductsWithInfoElectronico(
		product ? [product] : []
	);

	const currentProduct = useMemo(
		() => currentProducts?.[0] ?? product,
		[currentProducts, product]
	);

	return { product: currentProduct, data, isLoading: isLoading || isLoadingCurrent };
};
